import {
  buildSignupAnswersSchema,
  type SignupAnswerValue,
  type SignupField,
} from "@ternary/contracts"
import { Checkbox, Field, Input, MultiSelect, Select } from "@ternary/ui"
import { getCodes } from "country-list"
import { type ChangeEvent, type ReactElement, useMemo } from "react"
import { useTranslation } from "react-i18next"

type Answers = Record<string, SignupAnswerValue>

// English copy, which is also the translation key: the page passes each message through t().
const REQUIRED: Record<SignupField["kind"], string> = {
  checkbox: "Tick this box to continue.",
  country: "Choose a country.",
  date: "Enter a date.",
  multiselect: "Choose at least one option.",
  phone: "Fill in this field.",
  select: "Choose an option.",
  text: "Fill in this field.",
}

const INVALID: Record<SignupField["kind"], string> = {
  checkbox: "Tick this box to continue.",
  country: "Choose a country.",
  date: "Enter a date.",
  multiselect: "Choose one of the options.",
  phone: "Enter a phone number, such as +1 555 010 0199.",
  select: "Choose one of the options.",
  text: "Use 500 characters or fewer.",
}

function isEmpty(field: SignupField, value: SignupAnswerValue | undefined) {
  if (field.kind === "checkbox") return value !== true
  if (Array.isArray(value)) return value.length === 0
  return typeof value !== "string" || value.trim() === ""
}

/**
 * Each field's error message, by key: a required field left empty, or a value the contract would
 * refuse (`buildSignupAnswersSchema`). A field with no error is absent. The messages are English
 * keys, for the page to translate.
 */
export function validateSignupAnswers(
  fields: SignupField[],
  answers: Record<string, string | string[] | boolean>,
): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const field of fields) {
    const value = answers[field.key]
    if (isEmpty(field, value)) {
      if (field.required) errors[field.key] = REQUIRED[field.kind]
      continue
    }
    if (!buildSignupAnswersSchema([field]).safeParse({ [field.key]: value }).success) {
      errors[field.key] = INVALID[field.kind]
    }
  }
  return errors
}

/**
 * The `answers` of `POST …/details`: the form's fields only, trimmed, without an empty optional
 * value, which the strict contract would refuse. An optional checkbox is sent as given.
 */
export function signupAnswersToSend(fields: SignupField[], answers: Answers): Answers {
  const sent: Answers = {}
  for (const field of fields) {
    const value = answers[field.key]
    if (value === undefined) continue
    if (field.kind === "checkbox") {
      if (typeof value === "boolean") sent[field.key] = value
    } else if (!isEmpty(field, value)) {
      sent[field.key] = typeof value === "string" ? value.trim() : value
    }
  }
  return sent
}

/**
 * The fields in pages of at most `size`, in order, so no page of questions scrolls. A multiselect
 * is one row, because its options open in a popover.
 */
export function chunkSignupFields(fields: SignupField[], size = 4): SignupField[][] {
  const pages: SignupField[][] = []
  for (let at = 0; at < fields.length; at += size) pages.push(fields.slice(at, at + size))
  return pages
}

/** The country options, by ISO code, named in `locale` and sorted by that name. */
function countryOptions(locale: string) {
  const names = new Intl.DisplayNames([locale], { type: "region" })
  return getCodes()
    .map((code) => code.toUpperCase())
    .map((code) => ({ code, name: names.of(code) ?? code }))
    .sort((left, right) => left.name.localeCompare(right.name, locale))
}

/** One of the tenant's questions, by its kind. Its label is the tenant's own text, never translated. */
export function SignupFieldInput({
  field,
  value,
  onChange,
  error,
}: {
  field: SignupField
  value: string | string[] | boolean | undefined
  onChange(value: string | string[] | boolean): void
  error?: string | undefined
}): ReactElement {
  const { i18n, t } = useTranslation()
  const locale = i18n.resolvedLanguage ?? i18n.language ?? "en"
  const countries = useMemo(
    () => (field.kind === "country" ? countryOptions(locale) : []),
    [field.kind, locale],
  )
  const text = typeof value === "string" ? value : ""
  const onText = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    onChange(event.currentTarget.value)
  const common = { error, label: field.label, required: field.required }

  switch (field.kind) {
    case "multiselect":
      return (
        <MultiSelect
          {...common}
          onChange={onChange}
          options={(field.options ?? []).map((option) => ({ label: option, value: option }))}
          requiredLabel={t("Required")}
          searchLabel={t("Search")}
          summaryLabel={(count) => t("{{count}} selected", { count })}
          value={Array.isArray(value) ? value : []}
        />
      )
    case "checkbox":
      return (
        <Checkbox
          checked={value === true}
          error={error}
          label={field.label}
          onChange={(event) => onChange(event.currentTarget.checked)}
          required={field.required}
        />
      )
    case "select":
      return (
        <Field {...common}>
          <Select onChange={onText} value={text}>
            <option value="">{t("Choose one")}</option>
            {(field.options ?? []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>
      )
    case "country":
      return (
        <Field {...common}>
          <Select autoComplete="country" onChange={onText} value={text}>
            <option value="">{t("Choose your country")}</option>
            {countries.map(({ code, name }) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </Select>
        </Field>
      )
    case "phone":
      return (
        <Field {...common}>
          <Input autoComplete="tel" inputMode="tel" onChange={onText} type="tel" value={text} />
        </Field>
      )
    case "date":
      return (
        <Field {...common}>
          <Input onChange={onText} type="date" value={text} />
        </Field>
      )
    case "text":
      return (
        <Field {...common}>
          <Input maxLength={500} onChange={onText} type="text" value={text} />
        </Field>
      )
  }
}
